import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { decideOnNewItems, isNearBottom, pillBaseline } from '../utils/livePolling';

/**
 * P-60 (D-236): keeps a conversation pinned to the bottom ONLY when the reader is
 * already there (or sent the message); otherwise counts the new messages so the
 * caller can show "הודעה חדשה ↓" instead of jumping the scroll.
 * `containerRef` is the scroll pane; on phones the page itself scrolls, so the
 * document element is measured when the pane does not scroll.
 */
function scrollerOf(el: HTMLElement | null): HTMLElement | null {
  return el && el.scrollHeight > el.clientHeight + 1 ? el : (document.scrollingElement as HTMLElement | null);
}

export function useNewMessagePill(
  containerRef: RefObject<HTMLElement>,
  count: number,
  lastIsMine: boolean,
  scrollOnFirstRender = true,
  /** FIX-B38 (QX-840): the conversation's id - a change starts the counter over (no stale pill, scroll to the bottom). */
  resetKey?: unknown,
) {
  const prev = useRef<number | null>(null);
  const prevKey = useRef<unknown>(resetKey);
  const [pending, setPending] = useState(0);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight + 1) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    else el.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [containerRef]);

  useEffect(() => {
    const base = pillBaseline(prevKey.current, resetKey, prev.current);
    prevKey.current = resetKey;
    if (base.reset) {
      prev.current = base.prevCount;
      setPending(0);
    }
    const scroller = scrollerOf(containerRef.current);
    const near = scroller ? isNearBottom(scroller) : true;
    const d = decideOnNewItems({ prevCount: prev.current, nextCount: count, nearBottom: near, lastIsMine });
    const first = prev.current === null;
    prev.current = count;
    if (first && !scrollOnFirstRender) return undefined;
    if (d.autoScroll) {
      setPending(0);
      const id = window.setTimeout(scrollToBottom, 100);
      return () => window.clearTimeout(id);
    }
    if (d.addPending > 0) setPending((p) => p + d.addPending);
    return undefined;
  }, [count, lastIsMine, containerRef, scrollToBottom, scrollOnFirstRender, resetKey]);

  // Reaching the bottom by hand clears the pill.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || pending === 0) return undefined;
    const onScroll = () => { const sc = scrollerOf(el); if (!sc || isNearBottom(sc)) setPending(0); };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); window.removeEventListener('scroll', onScroll); };
  }, [containerRef, pending]);

  const jump = useCallback(() => { setPending(0); scrollToBottom(); }, [scrollToBottom]);
  return { pending, jump };
}
